import React from 'react';
import { Eye, Edit, ExternalLink } from 'lucide-react';
import { Customer, DealListItem } from '../../../types';
import { getStageBadgeStyle } from '../utils/dealStages';
import api from '../../../services/api';
import { showSuccess } from '../../../utils/toast';

export const DEALS_COLUMNS = (
  customers: Customer[],
  highlightedId: number | null,
  loadingDeal: boolean,
  updating: number | null,
  setUpdating: (id: number | null) => void,
  creatingProject: number | null,
  setCreatingProject: (id: number | null) => void,
  handleViewDetails: (d: DealListItem) => void,
  handleEdit: (d: DealListItem) => void,
  load: () => void,
  setHighlightedId: (id: number | null) => void,
  setError: (msg: string | null) => void,
  navigate: (path: string) => void,
) => [
  {
    id: 'name',
    label: 'Name',
    accessor: 'name',
    render: (d: DealListItem) => {
      const isHighlighted = highlightedId === d.id;
      return isHighlighted ? (
        <span
          data-deal-id={d.id}
          style={{ display: 'inline-flex', alignItems: 'center', gap: 6, color: '#15803d', fontWeight: 600 }}
        >
          <span style={{ width: 8, height: 8, background: '#22c55e', borderRadius: '50%', display: 'inline-block', animation: 'pulse 1s infinite' }} />
          {d.name}
        </span>
      ) : (
        <span data-deal-id={d.id} style={{ fontWeight: 500, color: 'var(--text-primary)' }}>{d.name}</span>
      );
    },
  },
  {
    id: 'customer',
    label: 'Customer',
    accessor: 'customer_id',
    render: (d: DealListItem) => {
      const name = customers.find((c) => c.id === d.customer_id)?.item;
      return (
        <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>
          {name ?? (d.customer_id ? `ID: ${d.customer_id}` : '-')}
        </span>
      );
    },
  },
  {
    id: 'stage',
    label: 'Stage',
    accessor: 'stage',
    render: (d: DealListItem) => (
      <span style={getStageBadgeStyle(d.stage)}>
        {d.stage}
      </span>
    ),
  },
  {
    id: 'project',
    label: 'Project',
    accessor: 'project',
    render: (d: DealListItem) =>
      d.project ? (
        <div style={{ display: 'flex', alignItems: 'center', gap: 4, fontSize: 13, color: 'var(--text-secondary)' }}>
          <span>{d.project.project_number}</span>
          <a
            href={`/projects?highlight=${d.project.id}`}
            onClick={(e) => { e.preventDefault(); navigate(`/projects?highlight=${d.project!.id}`); }}
            style={{ color: '#2563eb' }}
          >
            <ExternalLink style={{ width: 12, height: 12, display: 'inline' }} />
          </a>
        </div>
      ) : (
        <span style={{ color: 'var(--text-tertiary)' }}>-</span>
      ),
  },
  {
    id: 'created_at',
    label: 'Created',
    accessor: 'created_at',
    render: (d: DealListItem) => (
      <span style={{ fontSize: 13, color: 'var(--text-secondary)' }}>{new Date(d.created_at).toLocaleString()}</span>
    ),
  },
  {
    id: 'actions',
    label: 'Actions',
    accessor: 'id',
    render: (d: DealListItem) => (
      <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
        <button
          onClick={() => handleViewDetails(d)}
          disabled={loadingDeal}
          title="View Details"
          style={{ padding: '6px', color: '#2563eb', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(37,99,235,0.1)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
        >
          <Eye style={{ width: 16, height: 16 }} />
        </button>
        <button
          onClick={() => handleEdit(d)}
          disabled={loadingDeal}
          title="Edit"
          style={{ padding: '6px', color: '#7c3aed', background: 'transparent', border: 'none', borderRadius: 6, cursor: 'pointer' }}
          onMouseEnter={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'rgba(124,58,237,0.1)')}
          onMouseLeave={(e) => ((e.currentTarget as HTMLButtonElement).style.background = 'transparent')}
        >
          <Edit style={{ width: 16, height: 16 }} />
        </button>
        {d.stage !== 'Closed Won' && (
          <button
            disabled={updating === d.id}
            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: 'pointer', opacity: updating === d.id ? 0.5 : 1 }}
            onClick={async () => {
              try {
                setError(null);
                setUpdating(d.id);
                await api.put(`/deals/${d.id}`, { stage: 'Closed Won' });
                showSuccess('Deal marked as Closed Won successfully!');
                await load();
                setHighlightedId(d.id);
                setTimeout(() => setHighlightedId(null), 3000);
              } catch (e: any) {
                setError(e?.response?.data?.message || e?.message || 'Failed to update deal');
              } finally {
                setUpdating(null);
              }
            }}
          >
            {updating === d.id ? 'Updating...' : 'Mark Closed Won'}
          </button>
        )}
        {d.stage === 'Closed Won' && !d.project && (
          <button
            disabled={creatingProject === d.id}
            style={{ padding: '4px 10px', fontSize: 12, border: '1px solid var(--border-color)', borderRadius: 6, background: 'var(--card-bg)', color: 'var(--text-primary)', cursor: 'pointer', opacity: creatingProject === d.id ? 0.5 : 1 }}
            onClick={async () => {
              try {
                setError(null);
                setCreatingProject(d.id);
                const response = await api.post(`/projects/create-from-deal/${d.id}`);
                showSuccess('Project created successfully!');
                const newProjectId = (response as any)?.data?.id || (response as any)?.project?.id;
                navigate(newProjectId ? `/projects?highlight=${newProjectId}` : '/projects');
              } catch (e: any) {
                setError(e?.response?.data?.message || e?.message || 'Project creation failed');
              } finally {
                setCreatingProject(null);
              }
            }}
          >
            {creatingProject === d.id ? 'Creating...' : 'Create Project'}
          </button>
        )}
      </div>
    ),
  },
];
